import React from 'react';
import { NavLink } from 'react-router-dom';
import { History, ArrowRight, UserPlus, Coins } from 'lucide-react';
import { Money } from '@/components/common/Money';

interface Transaction {
  id: string;
  date: string;
  description: string;
  amountMinor: number;
  type: 'credit' | 'debit';
}

interface DashboardRecentActivityProps {
  transactions: Transaction[];
  referralUrl: string;
}

export const DashboardRecentActivity: React.FC<DashboardRecentActivityProps> = ({
  transactions,
  referralUrl,
}) => {
  return (
    <div className="bg-[#0E1015] border border-zinc-800/80 rounded-2xl p-6">
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <History className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Recent Referral Activity</h2>
            <p className="text-xs text-zinc-400">
              Live updates when referred merchants subscribe or renew
            </p>
          </div>
        </div>
        <NavLink
          to="/payouts"
          className="text-xs text-indigo-400 hover:text-indigo-300 font-medium flex items-center gap-1 transition"
        >
          <span>View all ledger</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </NavLink>
      </div>

      {transactions.length === 0 ? (
        <div className="py-12 px-4 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400">
            <Coins className="w-5 h-5 text-indigo-400" />
          </div>
          <h3 className="text-sm font-semibold text-white">No commissions recorded yet</h3>
          <p className="text-xs text-zinc-400 max-w-sm mx-auto mt-1 mb-5 leading-relaxed">
            When businesses register and subscribe using your referral link, your recurring commission
            earnings will appear here automatically.
          </p>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(referralUrl)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl text-xs font-medium border border-zinc-700/80 transition cursor-pointer shadow-xs"
          >
            <UserPlus className="w-3.5 h-3.5 text-indigo-400" />
            <span>Copy Referral Link & Invite Merchants</span>
          </button>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/60 text-xs">
          {transactions.map((tx) => (
            <div key={tx.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{tx.description}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{tx.date}</p>
              </div>
              <div className="text-right font-mono font-semibold">
                <span className={tx.type === 'credit' ? 'text-emerald-400' : 'text-zinc-300'}>
                  {tx.type === 'credit' ? '+' : ''}
                  <Money amountMinor={tx.amountMinor} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
